import { createFileRoute, redirect } from '@tanstack/react-router'
import { format } from 'date-fns'
import { startOfMonth, subMonths } from 'date-fns'
import { parse } from 'valibot'

import { getAvailableDcsSelectQueryOptions } from '@/features/dcs/query-options'
import {
  fetchMonthlyCustomerReports,
  fetchMonthlyCustomerReportSummaries,
  type FetchMonthlyCustomerReportsQuery,
  type FetchMonthlyCustomerReportSummariesQuery
} from '@/features/monthly-customer-reports/api'
import { isDateRangeMode } from '@/features/shared/models'
import {
  selectedDcIdAtom,
  tenantStore
} from '@/routes/_protected/$tenantKey/-states'

import { MonthlyCustomerReportSearchValidateSchema } from './-models/monthly-customer-report-search'

export const Route = createFileRoute(
  '/_protected/$tenantKey/_performance_managements/monthly-customer-reports/'
)({
  validateSearch: (search) =>
    parse(MonthlyCustomerReportSearchValidateSchema, search),
  loaderDeps: ({ search }) => search,
  beforeLoad: async ({
    search,
    location,
    context: { auth, queryClient },
    params: { tenantKey }
  }) => {
    const availableDcs = await queryClient.ensureQueryData(
      getAvailableDcsSelectQueryOptions(tenantKey, auth.user)
    )
    const availableDcIds = availableDcs.map((x) => x.id)
    const filteredDcIds = search.dcIds?.filter((x) =>
      availableDcIds.includes(x)
    )
    const selectedDcId = tenantStore.get(selectedDcIdAtom)

    // 検索パラメータが未指定の場合、yearMonthFromに前月、yearMonthToに当月を初期値として設定
    const currentMonth = format(startOfMonth(new Date()), 'yyyy-MM')
    const previousMonth = format(
      startOfMonth(subMonths(new Date(), 1)),
      'yyyy-MM'
    )
    // 日付範囲モードでは計上日の from/to が必須なので、未指定なら当月 1 日から本日を入れる (#4421)
    if (
      isDateRangeMode(search.searchMode) &&
      (!search.operatingDateFrom || !search.operatingDateTo)
    ) {
      throw redirect({
        to: location.pathname,
        search: {
          ...search,
          operatingDateFrom:
            search.operatingDateFrom ??
            format(startOfMonth(new Date()), 'yyyy-MM-dd'),
          operatingDateTo:
            search.operatingDateTo ?? format(new Date(), 'yyyy-MM-dd'),
          dcIds:
            filteredDcIds && filteredDcIds.length > 0
              ? filteredDcIds
              : [selectedDcId]
        }
      })
    }

    const needsYearMonthDefault =
      !isDateRangeMode(search.searchMode) &&
      !search.yearMonthFrom &&
      !search.yearMonthTo

    if (needsYearMonthDefault) {
      throw redirect({
        to: location.pathname,
        search: {
          ...search,
          yearMonthFrom: previousMonth,
          yearMonthTo: currentMonth,
          dcIds:
            filteredDcIds && filteredDcIds.length > 0
              ? filteredDcIds
              : [selectedDcId]
        }
      })
    }

    if (!filteredDcIds || filteredDcIds.length === 0) {
      throw redirect({
        to: location.pathname,
        search: {
          ...search,
          dcIds: [selectedDcId]
        }
      })
    }

    if (search.dcIds?.length !== filteredDcIds.length) {
      throw redirect({
        to: location.pathname,
        search: {
          ...search,
          dcIds: filteredDcIds
        }
      })
    }
  },
  loader: async ({
    abortController,
    params: { tenantKey },
    deps,
    context: { auth }
  }) => {
    // 日付範囲モードでは行の単位が荷主に変わるため、別のエンドポイントを叩く (#4421)
    if (isDateRangeMode(deps.searchMode)) {
      const summaryQuery: FetchMonthlyCustomerReportSummariesQuery = {
        tenantKey,
        // beforeLoad で operatingDateFrom/To が未設定の場合はリダイレクトされるため、ここでは必ず値が存在する
        query: deps as FetchMonthlyCustomerReportSummariesQuery['query']
      }
      return {
        authUser: auth.user,
        tenantKey,
        monthlyCustomerReports: null,
        monthlyCustomerReportSummaries:
          await fetchMonthlyCustomerReportSummaries(
            summaryQuery,
            abortController.signal
          )
      }
    }

    const query: FetchMonthlyCustomerReportsQuery = {
      tenantKey,
      // beforeLoad で yearMonthFrom/yearMonthTo が未設定の場合はリダイレクトされるため、ここでは必ず値が存在する
      query: deps as FetchMonthlyCustomerReportsQuery['query']
    }
    return {
      authUser: auth.user,
      tenantKey,
      monthlyCustomerReports: await fetchMonthlyCustomerReports(
        query,
        abortController.signal
      ),
      monthlyCustomerReportSummaries: null
    }
  }
})
